import apiClient from './apiClient';
import { handleApiError, formatApiResponse } from './api';

/**
 * Servicio para gestión de procesos de producción
 * Basado en la API documentada en procesos_produccion_module.md
 *
 * Tipos de procesos disponibles:
 * - ENCAPSULATION: Encapsulado de cápsulas
 * - TEA_PACKING: Empaque de tés
 * - PACKAGING: Empacado general
 * - CLEANING: Limpieza
 * - RECEIVING: Recepción
 * - RETURNS: Retornos
 */
const procesosService = {
  // ==================== GESTIÓN BÁSICA DE PROCESOS ====================

  /**
   * Obtener lista de procesos de producción
   * @param {Object} params - Parámetros de filtrado
   * @param {number} params.skip - Offset para paginación
   * @param {number} params.limit - Límite de resultados
   * @param {string} params.process_type - Tipo de proceso
   * @param {string} params.status - Estado del proceso
   * @param {string} params.quality_status - Estado de calidad
   * @param {number} params.product_id - ID del producto
   * @param {string} params.operator_name - Nombre del operador
   * @param {string} params.process_date_from - Fecha desde
   * @param {string} params.process_date_to - Fecha hasta
   * @param {string} params.batch_number - Número de lote
   */
  getProcesos: async (params = {}) => {
    try {
      const response = await apiClient.get('/production-processes/', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener proceso por ID
   */
  getProceso: async (id) => {
    try {
      const response = await apiClient.get(`/production-processes/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Crear nuevo proceso de producción
   * @param {Object} data - Datos del proceso
   * @param {string} data.process_type - Tipo de proceso (ENCAPSULATION, TEA_PACKING, etc.)
   * @param {Object} data.production_process - Datos generales del proceso
   * @param {Object} data.process_details - Detalles específicos según el tipo
   */
  createProceso: async (data) => {
    try {
      console.log('Creando proceso con datos:', data);
      const response = await apiClient.post('/production-processes/', data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en createProceso:', error);
      return handleApiError(error);
    }
  },

  /**
   * Actualizar proceso de producción
   */
  updateProceso: async (id, data) => {
    try {
      console.log('Actualizando proceso con datos:', data);
      const response = await apiClient.put(`/production-processes/${id}`, data);
      return formatApiResponse(response);
    } catch (error) {
      console.error('Error en updateProceso:', error);
      return handleApiError(error);
    }
  },

  /**
   * Eliminar proceso (solo PENDING o CANCELLED)
   */
  deleteProceso: async (id) => {
    try {
      const response = await apiClient.delete(`/production-processes/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== DETALLES ESPECÍFICOS POR TIPO ====================

  /**
   * Obtener detalles de proceso de encapsulado
   */
  getEncapsulationDetails: async (processId) => {
    try {
      const response = await apiClient.get(`/production-processes/encapsulation/${processId}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener detalles de proceso de empaque de tés
   */
  getTeaPackingDetails: async (processId) => {
    try {
      const response = await apiClient.get(`/production-processes/tea-packing/${processId}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener detalles de proceso de limpieza
   */
  getCleaningDetails: async (processId) => {
    try {
      const response = await apiClient.get(`/production-processes/cleaning/${processId}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Obtener detalles de proceso de retorno
   */
  getReturnsDetails: async (processId) => {
    try {
      const response = await apiClient.get(`/production-processes/returns/${processId}`);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Actualizar proceso de retorno
   */
  updateReturns: async (processId, data) => {
    try {
      const response = await apiClient.put(`/production-processes/returns/${processId}`, data);
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== ESTADÍSTICAS Y REPORTES ====================

  /**
   * Obtener estadísticas generales de producción
   */
  getStats: async (params = {}) => {
    try {
      const response = await apiClient.get('/production-processes/stats/overview', { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  /**
   * Listar procesos por tipo específico
   */
  getByType: async (processType, params = {}) => {
    try {
      const response = await apiClient.get(`/production-processes/by-type/${processType}`, { params });
      return formatApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  },

  // ==================== UTILIDADES ====================

  /**
   * Obtener tipos de procesos disponibles
   */
  getProcessTypes: () => {
    return [
      { value: 'ENCAPSULATION', label: 'Encapsulado' },
      { value: 'TEA_PACKING', label: 'Empaque de Tés' },
      { value: 'PACKAGING', label: 'Empacado General' },
      { value: 'CLEANING', label: 'Limpieza' },
      { value: 'RECEIVING', label: 'Recepción' },
      { value: 'RETURNS', label: 'Retornos' },
      { value: 'MAINTENANCE', label: 'Mantenimiento' }
    ];
  },

  /**
   * Obtener estados de proceso
   */
  getProcessStatuses: () => {
    return [
      { value: 'PENDING', label: 'Pendiente' },
      { value: 'IN_PROGRESS', label: 'En Progreso' },
      { value: 'COMPLETED', label: 'Completado' },
      { value: 'PAUSED', label: 'Pausado' },
      { value: 'CANCELLED', label: 'Cancelado' }
    ];
  },

  /**
   * Obtener estados de calidad
   */
  getQualityStatuses: () => {
    return [
      { value: 'PENDING', label: 'Pendiente' },
      { value: 'APPROVED', label: 'Aprobado' },
      { value: 'REJECTED', label: 'Rechazado' },
      { value: 'REVIEW_REQUIRED', label: 'Requiere Revisión' }
    ];
  },

  /**
   * Validar datos de proceso según el tipo
   */
  validateProcessData: (processType, data) => {
    const errors = {};

    // Validaciones comunes
    if (!data.production_process?.operator_name) {
      errors.operator_name = 'El nombre del operador es requerido';
    }
    if (!data.production_process?.process_date) {
      errors.process_date = 'La fecha del proceso es requerida';
    }

    // Validaciones específicas por tipo
    switch (processType) {
      case 'ENCAPSULATION':
        if (!data.process_details?.net_weight_per_capsule) {
          errors.net_weight = 'El peso neto por cápsula es requerido';
        }
        if (!data.process_details?.capsules_per_bottle) {
          errors.capsules = 'La cantidad de cápsulas por bote es requerida';
        }
        break;

      case 'TEA_PACKING':
        if (!data.process_details?.tea_bags_per_box) {
          errors.tea_bags = 'La cantidad de bolsitas por caja es requerida';
        }
        break;

      case 'CLEANING':
        const hasArea = data.process_details?.production_area ||
                       data.process_details?.packaging_area ||
                       data.process_details?.warehouse_area ||
                       data.process_details?.office_area ||
                       data.process_details?.bathroom_area ||
                       data.process_details?.common_areas;

        if (!hasArea) {
          errors.areas = 'Debe seleccionar al menos un área de limpieza';
        }
        break;

      case 'RETURNS':
        if (!data.process_details?.client_id) {
          errors.client = 'El cliente es requerido';
        }
        if (!data.process_details?.return_reason) {
          errors.reason = 'El motivo del retorno es requerido';
        }
        break;

      case 'RECEIVING':
        if (!data.process_details?.supplier_name) {
          errors.supplier = 'El proveedor es requerido';
        }
        if (!data.process_details?.quantity_received) {
          errors.quantity = 'La cantidad recibida es requerida';
        }
        break;

      case 'MAINTENANCE':
        if (!data.process_details?.equipment_name) {
          errors.equipment = 'El nombre del equipo es requerido';
        }
        if (!data.process_details?.maintenance_type) {
          errors.maintenance_type = 'El tipo de mantenimiento es requerido';
        }
        if (!data.process_details?.technician_name) {
          errors.technician = 'El nombre del técnico es requerido';
        }
        break;
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }
};

export default procesosService;
